import {
  DELETION,
  Empty,
  KeyStr,
  NONE,
  NullElement,
  PLACEMENT,
  RefStr,
  StyleStr,
  UPDATE,
} from "../constants";
import { dispatchEffects } from "../hooks/hooks";
import { Fiber, MutableRefObject } from "../types";
import {
  equals,
  fastRecursive,
  has,
  isChildren,
  isFn,
  isTextNode,
} from "../utils";

const deletions: Fiber[] = [];

export function pushDeletion(fiber: Fiber) {
  deletions.push(fiber);
}

export function commitRoot(workInProgressRoot: Fiber) {
  deletions.forEach(commitDeletions);
  dispatchEffects(workInProgressRoot, undefined);
  commitWork(workInProgressRoot!.child!);
  deletions.length = 0;
}

function getParentNode(fiber: Fiber) {
  let domParentFiber = fiber.parent;
  while (!domParentFiber?.dom) {
    domParentFiber = domParentFiber?.parent!;
  }

  return domParentFiber;
}

function commitDeletions(current: Fiber) {
  if (equals(current.flag, DELETION) && !equals(current.type, NullElement)) {
    commitDeletion(current);
  }
}

const commitWork = fastRecursive<Fiber | undefined, void>(function* (current) {
  if (!current) {
    return;
  }

  const fiberTag = current.flag;

  if (!equals(fiberTag, NONE)) {
    if (isFn(current.type)) {
      dispatchEffects(current, fiberTag);
    }

    if (!equals(current.dom, null) && !equals(current.type, NullElement)) {
      const domParentFiber = getParentNode(current);
      if (equals(current.flag, PLACEMENT)) {
        commitPlacement(domParentFiber, current, domParentFiber.dom!);
      } else if (equals(current.flag, UPDATE)) {
        commitUpdate(current.dom, current.alternate!.props!, current.props!);
      }
    }
  }

  if (current.child) {
    yield current.child;
  }
  if (current.sibling) {
    yield current.sibling;
  }

  applyRef(current);
});

function getFirstChildFiber(domParentFiber: Fiber) {
  let firstchildfiber = domParentFiber.child;

  while (firstchildfiber?.dom == null) {
    firstchildfiber = firstchildfiber?.child;
  }

  return firstchildfiber;
}

function commitPlacement(
  domParentFiber: Fiber,
  fiber: Fiber,
  domParent: HTMLElement
) {
  const firstchildfiber = getFirstChildFiber(domParentFiber);
  if (equals(firstchildfiber, fiber)) {
    if (!equals(domParent.firstChild, fiber.dom)) {
      if (fiber.dom) {
        domParent.insertBefore(fiber.dom, domParent.firstChild);
      }
    }
  } else {
    if (fiber.dom) {
      domParent?.appendChild(fiber.dom);
    }
  }
}

function applyRef(fiber: Fiber) {
  if (fiber.ref) {
    const refNode = fiber.dom;

    isFn(fiber.ref)
      ? fiber.ref(refNode)
      : ((fiber.ref as MutableRefObject<any>).current = refNode);
  }
}

export function commitDeletion(fiber: Fiber | undefined) {
  if (!fiber) return;

  if (fiber.dom) {
    fiber.dom.parentNode?.removeChild(fiber.dom);
  } else if ((fiber.props?.children.length || 0) > 0) {
    for (let value of fiber.props.children) {
      commitDeletion(value);
    }
  } else {
    commitDeletion(fiber.child);
  }

  fiber!.flag = undefined;
  dispatchEffects(fiber, DELETION);
}

export function commitUpdate<P extends Record<string, any>>(
  dom: HTMLElement,
  oldProps: P,
  newProps: P
) {
  const props = { ...oldProps, ...newProps };
  for (let [name, b] of Object.entries(props)) {
    const a = oldProps[name];

    if (
      equals(a, b) ||
      isChildren(name) ||
      equals(name, KeyStr) ||
      equals(name, RefStr)
    ) {
      continue;
    }
    if (equals(name, StyleStr) && !isTextNode(b)) {
      for (const [styleKey, bStyle] of Object.entries(b)) {
        const aStyle = a[styleKey];
        if (!equals(aStyle, bStyle)) {
          (dom as any).style[styleKey] = bStyle || Empty;
        }
      }
      continue;
    }
    if (has(name, "o") && has(name, "n", 1)) {
      name = name.slice(2).toLowerCase();
      if (a) dom.removeEventListener(name, a);
      dom.addEventListener(name, b);
      continue;
    }
    if (name in dom && !(dom instanceof SVGElement)) {
      (dom as any)[name] = b || Empty;
      continue;
    }
    if (b == undefined || equals(b, false)) {
      dom.removeAttribute(name);
    } else {
      dom.setAttribute(name, b);
    }
  }
  return dom;
}
